import type { EditorSet, EditorGet, UiSlice } from "../types";

export function createUiSlice(set: EditorSet, _get: EditorGet): UiSlice {
    return {
        selectedSceneId: null,
        selection: { kind: "none" },
        activeTextEditorId: null,
        pendingCaret: null,
        liveTransform: null,
        liveKind: null,
        liveGuides: [],
        activeTab: "scenes",
        showFloatingNav: true,
        save: { isSaving: false, saveError: null, lastSaved: null },
        isGeneratingSceneAudio: {},
        interactionModal: { open: false },
        renderModalOpen: false,
        activeRenderRunId: null,

        setSelectedSceneId: (sceneId) =>
            set((state) => {
                state.selectedSceneId = sceneId;
                state.selection = { kind: "none" };
                state.activeTextEditorId = null;
                state.pendingCaret = null;
            }),

        selectElement: (elementId) =>
            set((state) => {
                // Leaving a text element that was being edited for a different
                // element ends its edit mode (focusing the same one keeps it).
                if (
                    state.activeTextEditorId &&
                    state.activeTextEditorId !== elementId
                ) {
                    state.activeTextEditorId = null;
                    state.pendingCaret = null;
                }
                state.selection = { kind: "element", elementId };
            }),

        selectScene: () =>
            set((state) => {
                state.selection = { kind: "scene" };
                state.activeTextEditorId = null;
                state.pendingCaret = null;
            }),

        clearSelection: () =>
            set((state) => {
                state.selection = { kind: "none" };
                state.activeTextEditorId = null;
                state.pendingCaret = null;
            }),

        setSelectedBlockImage: (target) =>
            set((state) => {
                state.selection = target
                    ? {
                          kind: "block-image",
                          elementId: target.elementId,
                          pos: target.pos,
                      }
                    : { kind: "none" };
            }),

        setActiveTextEditorId: (id) =>
            set((state) => {
                state.activeTextEditorId = id;
                if (id === null) state.pendingCaret = null;
            }),

        // Enter text-edit mode for an element, optionally remembering the
        // viewport point where the caret should land (set from a click).
        enterTextEdit: (elementId, caret) =>
            set((state) => {
                state.selection = { kind: "element", elementId };
                state.activeTextEditorId = elementId;
                state.pendingCaret = caret;
            }),

        clearPendingCaret: () =>
            set((state) => {
                state.pendingCaret = null;
            }),

        beginLiveTransform: (elementId, transform) =>
            set((state) => {
                state.liveTransform = { elementId, transform };
            }),

        setLiveFrame: ({ transform, kind, guides }) =>
            set((state) => {
                if (state.liveTransform) state.liveTransform.transform = transform;
                state.liveKind = kind;
                state.liveGuides = guides;
            }),

        clearLiveGuides: () =>
            set((state) => {
                state.liveGuides = [];
            }),

        endLiveTransform: () =>
            set((state) => {
                state.liveTransform = null;
                state.liveKind = null;
                state.liveGuides = [];
            }),

        setActiveTab: (tab) =>
            set((state) => {
                state.activeTab = tab;
            }),

        onChangeActiveTab: (tab) =>
            set((state) => {
                state.activeTab =
                    tab === state.activeTab && tab !== "closed" ? "closed" : tab;
            }),

        setShowFloatingNav: (show) =>
            set((state) => {
                state.showFloatingNav = show;
            }),

        setSaveStatus: (status) =>
            set((state) => {
                state.save = status;
            }),

        setGeneratingSceneAudio: (sceneId, generating) =>
            set((state) => {
                state.isGeneratingSceneAudio[sceneId] = generating;
            }),

        openInteractionCreate: (gapId) =>
            set((state) => {
                state.interactionModal = { open: true, mode: "create", gapId };
            }),

        openInteractionEdit: (gapId, interactionId) =>
            set((state) => {
                state.interactionModal = {
                    open: true,
                    mode: "edit",
                    gapId,
                    interactionId,
                };
            }),

        closeInteractionModal: () =>
            set((state) => {
                state.interactionModal = { open: false };
            }),

        setRenderModalOpen: (open) =>
            set((state) => {
                state.renderModalOpen = open;
            }),

        setActiveRenderRunId: (runId) =>
            set((state) => {
                state.activeRenderRunId = runId;
            }),
    };
}
